/**
 * Access Reviews (spec docs/superpowers/specs/2026-10-26-access-reviews-design.md): the rules, once.
 *
 *   ACCESS_REVIEW_DEFAULT_ROLES   the 13 privileged roles plus Global Reader (Hugo Andrade, 2026-10-26, decision 2)
 *   assignmentKey, keyTag         the identity of an assignment (principal, role, scope, kind) and its short hash
 *   signalsOf, riskSignals        the signal codes a review item carries, in words, and which ones are a risk
 *   validateDecision              Keep, Remove, Not sure with a quick reason or text; text is required on risk items
 *   bulkEligible, needsStepUp     bulk excludes Global Administrator, Privileged Role Administrator and risky items;
 *                                 a Keep on those two roles through an email link needs a one-time code (decision 1)
 *   resolveReviewer               designated, then manager, then the admin fallback; never the holder (no self-review)
 *   nextStartDate, dueDateFor     the schedule (quarterly on the second Monday of Jan, Apr, Jul, Oct by default)
 *   canTransition                 the campaign state machine of spec 6.1
 *   ac67Result                    the AC-6(7) result rule of spec 9.2 (Met, Partially met, Not met)
 *   validateSettings, validateNote, normalizeNote   the setup panel and role assignment notes (Hugo, items 5 and 6)
 *   buildFindings                 "Findings between campaigns" (ARV-001 to ARV-007) from ids and counts only
 *   sanitizeAccessReviewSummary   the run callback keeps only the documented, bounded, names-free shape
 *   sanitizeAccessReviewDetail    the bucket file the portal reads at request time (names, never stored here)
 *
 * Expected counts per role are optional and blank by default (Hugo, item 5): a count finding (ARV-001) appears only
 * where the admin set an expectation. The Microsoft Global Administrator guidance (IDENT-003, 2 to 4) is a check, not
 * an expectation, and is shown whatever the admin set.
 *
 * Phase 2 seam (spec section 18, "Expectations"): role bundles plug in as another source of `expectations` passed to
 * buildFindings; the v1 expectation is a per-role count only (EXPECTATION_KINDS).
 *
 * Pure. No AWS, no fetch, no Date.now() unless a `now` is not given. Shared by the portal backend, the frontend and the
 * dev mocks. The collector keeps its own copy of the role list (lambda/access-review-insights.mjs), pinned by a drift test.
 */

// ---- Roles ----------------------------------------------------------------------------------------------------

export const GLOBAL_ADMINISTRATOR = '62e90394-69f5-4237-9190-012177145e10';
export const PRIVILEGED_ROLE_ADMINISTRATOR = 'e8611ab8-c189-46e8-94e1-60213ab1f814';
export const GLOBAL_READER = 'f2ef992c-3afb-46b9-b7cf-a126ee74c451';

/** The default scope (decision 2): the IPC Global privileged list (lambda/privileged-roles.mjs) plus Global Reader. */
export const ACCESS_REVIEW_DEFAULT_ROLES = Object.freeze([
  Object.freeze({ id: GLOBAL_ADMINISTRATOR, name: 'Global Administrator' }),
  Object.freeze({ id: PRIVILEGED_ROLE_ADMINISTRATOR, name: 'Privileged Role Administrator' }),
  Object.freeze({ id: '7be44c8a-adaf-4e2a-84d6-ab2649e08a13', name: 'Privileged Authentication Administrator' }),
  Object.freeze({ id: '194ae4cb-b126-40b2-bd5b-6091b380977d', name: 'Security Administrator' }),
  Object.freeze({ id: '29232cdf-9323-42fd-ade2-1d097af3e4de', name: 'Exchange Administrator' }),
  Object.freeze({ id: 'f28a1f50-f6e7-4571-818b-6a12f2af6b6c', name: 'SharePoint Administrator' }),
  Object.freeze({ id: 'fe930be7-5e62-47db-91af-98c3a49a38b1', name: 'User Administrator' }),
  Object.freeze({ id: 'b1be1c3e-b65d-4f19-8427-f6fa0d97feb9', name: 'Conditional Access Administrator' }),
  Object.freeze({ id: 'c4e39bd9-1100-46d3-8c65-fb160da0071f', name: 'Authentication Administrator' }),
  Object.freeze({ id: '9b895d92-2cd3-44c7-9d02-a6ac2d5ea5c3', name: 'Application Administrator' }),
  Object.freeze({ id: '158c047a-c907-4556-b7ef-446551a6b5f7', name: 'Cloud Application Administrator' }),
  Object.freeze({ id: '8ac3fc64-6eca-42ea-9e69-59f4c7b60eb2', name: 'Hybrid Identity Administrator' }),
  Object.freeze({ id: '3a2c62db-5318-420d-8d74-23affee5d9d5', name: 'Intune Administrator' }),
  Object.freeze({ id: GLOBAL_READER, name: 'Global Reader' }),
]);
export const DEFAULT_ROLE_IDS = Object.freeze(ACCESS_REVIEW_DEFAULT_ROLES.map((r) => r.id));
const DEFAULT_ROLE_NAMES = new Map(ACCESS_REVIEW_DEFAULT_ROLES.map((r) => [r.id, r.name]));

/** A Keep on these roles through an email link needs a one-time code (decision 1); they are never in a bulk decision. */
export const STEP_UP_ROLE_IDS = Object.freeze([GLOBAL_ADMINISTRATOR, PRIVILEGED_ROLE_ADMINISTRATOR]);
export const NO_BULK_ROLE_IDS = STEP_UP_ROLE_IDS;

/** The name of a role: from the collected role definitions when given, else the default list, else a neutral word. */
export function roleName(roleTemplateId, roles) {
  const fromDetail = Array.isArray(roles) ? roles.find((r) => r?.roleTemplateId === roleTemplateId)?.name : null;
  return (typeof fromDetail === 'string' && fromDetail) || DEFAULT_ROLE_NAMES.get(roleTemplateId) || 'Directory role';
}

// ---- Identity of an assignment ---------------------------------------------------------------------------------

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const SCOPE_RE = /^\/[A-Za-z0-9/_-]{0,200}$/;
export const KINDS = Object.freeze(['active', 'eligible']);
export const PRINCIPAL_TYPES = Object.freeze(['user', 'group', 'servicePrincipal', 'unknown']);
const lower = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');
export const isGuid = (v) => typeof v === 'string' && GUID_RE.test(v.toLowerCase());
export const isScope = (v) => typeof v === 'string' && SCOPE_RE.test(v);

/** `principalId|roleTemplateId|directoryScopeId|kind`, lower case: the same assignment in every run and every store. */
export function assignmentKey({ principalId, roleTemplateId, directoryScopeId = '/', kind = 'active' } = {}) {
  return [lower(principalId), lower(roleTemplateId), String(directoryScopeId ?? '/'), kind === 'eligible' ? 'eligible' : 'active'].join('|');
}

/** The same assignment ignoring the kind (an eligible assignment made active is not a removal, spec 7.1). */
export const assignmentIdentity = (a) => assignmentKey({ ...a, kind: 'active' }).replace(/\|active$/, '');

/**
 * A short, stable tag of an assignment key (FNV-1a 64 bit as 16 hex characters). Used where only "same or not" is
 * needed (ARV-003 compares the current assignments with the last closed campaign); it is not a secret and not a
 * security boundary. Pure JavaScript so the browser and Node.js agree.
 */
export function keyTag(key) {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  const s = String(key);
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x5bd1e995) >>> 0;
  }
  return h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0');
}

// ---- Signals ---------------------------------------------------------------------------------------------------

/** Signal codes the collector writes (lambda/access-review-insights.mjs) and their words (spec 8.2, last paragraph). */
export const SIGNALS = Object.freeze({
  guest: 'Guest',
  disabled: 'Disabled',
  'never-signed-in': 'Never signed in',
  'no-recent-sign-in': 'No sign-in in 90 days',
  standing: 'Standing, not eligible',
  group: 'Through a group',
  app: 'App (service principal)',
  synced: 'Synced from on-premises',
  'many-roles': 'Holds 3 or more roles',
});
export const SIGNAL_CODES = Object.freeze(Object.keys(SIGNALS));
const RISK_CODES = new Set(['guest', 'disabled', 'never-signed-in', 'no-recent-sign-in']);

/** The risk signals of an item: the four account signals, plus a standing Global Administrator (spec 6.5). */
export function riskSignals(item) {
  const signals = Array.isArray(item?.signals) ? item.signals : [];
  const out = signals.filter((s) => RISK_CODES.has(s));
  if (signals.includes('standing') && item?.roleTemplateId === GLOBAL_ADMINISTRATOR) out.push('standing');
  return out;
}
export const hasRisk = (item) => riskSignals(item).length > 0;

/** One line of words per signal, for a row ("Last sign-in 41 days ago", "Guest from fabrikam.com"). */
export function signalWords(item, holder) {
  const out = [];
  for (const code of Array.isArray(item?.signals) ? item.signals : []) {
    if (!Object.hasOwn(SIGNALS, code)) continue;
    if (code === 'guest' && holder?.homeDomain) out.push({ code, label: `Guest from ${holder.homeDomain}`, risk: true });
    else if (code === 'no-recent-sign-in' && Number.isInteger(item.daysSinceSignIn)) out.push({ code, label: `Last sign-in ${item.daysSinceSignIn} days ago`, risk: true });
    else out.push({ code, label: SIGNALS[code], risk: RISK_CODES.has(code) || (code === 'standing' && item.roleTemplateId === GLOBAL_ADMINISTRATOR) });
  }
  return out;
}

// ---- Decisions -------------------------------------------------------------------------------------------------

export const DECISIONS = Object.freeze(['keep', 'remove', 'not-sure']);
export const DECISION_WORDS = Object.freeze({ keep: 'Keep', remove: 'Remove', 'not-sure': 'Not sure', none: 'No decision' });
export const QUICK_REASONS = Object.freeze({
  keep: Object.freeze([
    Object.freeze({ id: 'daily-work', label: 'Needed for daily work' }),
    Object.freeze({ id: 'emergency-access', label: 'Emergency access account' }),
    Object.freeze({ id: 'service-owner', label: 'Service owner' }),
    Object.freeze({ id: 'should-be-eligible', label: 'Should be eligible, not standing' }),
  ]),
  remove: Object.freeze([
    Object.freeze({ id: 'no-longer-needed', label: 'No longer needs it' }),
    Object.freeze({ id: 'left-role', label: 'Left the role' }),
    Object.freeze({ id: 'duplicate-account', label: 'Duplicate account' }),
    Object.freeze({ id: 'contract-ended', label: 'Contract ended' }),
  ]),
});
export const TEXT_MIN = 10;
export const TEXT_MAX = 500;
const SECRET_PATTERNS = Object.freeze([
  /AKIA[0-9A-Z]{16}/,
  /-----BEGIN/i,
  /password\s*[:=]/i,
  /client_secret/i,
  /secret\s*[:=]/i,
  /token\s*[:=]/i,
  /eyJ[A-Za-z0-9_-]{10,}\./,
]);
export const looksSecret = (s) => SECRET_PATTERNS.some((re) => re.test(String(s)));
// Control and format characters (bidirectional overrides included) never reach evidence.
const UNSAFE = /[\p{Cc}\p{Cf}\u2028\u2029]/gu;
export const cleanText = (v) => (typeof v === 'string' ? v.replace(UNSAFE, ' ').replace(/\s+/g, ' ').trim() : '');
const cpLen = (s) => Array.from(s).length;

export const SECRET_TEXT_MESSAGE = 'Write the reason in plain words. Text such as "password:", "token:" or "secret:" is refused even when no real secret is present, because it is kept as audit evidence.';

/** Words for a quick reason id, or null. */
export const quickReasonLabel = (decision, reasonId) => (QUICK_REASONS[decision] ?? []).find((r) => r.id === reasonId)?.label ?? null;

/**
 * One decision as a reviewer submits it. `item` carries `roleTemplateId` and `signals` (risk decides whether text is
 * required). Returns { ok: true, value: { decision, reasonId, text } } or { ok: false, error } with words that say what
 * to change. Never trusts the browser: the route calls this for every decision, bulk ones included.
 */
/** The fields a decision may carry in a request; anything else is refused by the routes (never an admin flag). */
export const DECISION_FIELDS = Object.freeze(['itemRef', 'version', 'decision', 'reasonId', 'text', 'askToId']);
/** True unless a decision object carries a field outside DECISION_FIELDS (shape errors are left to `decide`). */
export const onlyDecisionFields = (decisions) => !Array.isArray(decisions) || decisions.every((x) => !x || typeof x !== 'object' || Object.keys(x).every((k) => DECISION_FIELDS.includes(k)));
export const ASK_TO_MESSAGE = 'Choose the colleague from your directory: type a name or an email address and pick the person.';

/**
 * `askToId` (optional, Not sure only): the directory id of a colleague who could answer, picked from the directory (spec
 * 2026-10-29 section 2 item 6: the server receives ids, never typed addresses, and resolves the address itself). The
 * item is then handed to that person (one hop) instead of the administrators. Returned lower case; the key is absent
 * when unset.
 */
export function validateDecision({ decision, reasonId, text, askToId } = {}, item = {}, { selfReview = false } = {}) {
  if (!DECISIONS.includes(decision)) return { ok: false, error: 'Choose Keep, Remove or Not sure.' };
  const t = cleanText(text);
  if (t && cpLen(t) > TEXT_MAX) return { ok: false, error: `Keep the reason under ${TEXT_MAX} characters.` };
  if (t && looksSecret(t)) return { ok: false, error: SECRET_TEXT_MESSAGE };
  const asking = askToId !== undefined && askToId !== null;
  if (asking && decision !== 'not-sure') return { ok: false, error: 'Only Not sure can name a colleague who could answer.' };
  if (decision === 'not-sure') {
    if (cpLen(t) < TEXT_MIN) return { ok: false, error: `Add a note of at least ${TEXT_MIN} characters for the next reviewer: what you know and what you are unsure about.` };
    if (!asking) return { ok: true, value: { decision, reasonId: null, text: t } };
    if (!isGuid(askToId)) return { ok: false, error: ASK_TO_MESSAGE };
    return { ok: true, value: { decision, reasonId: null, text: t, askToId: askToId.toLowerCase() } };
  }
  const reason = reasonId === undefined || reasonId === null || reasonId === '' ? null : reasonId;
  if (reason !== null && !quickReasonLabel(decision, reason)) return { ok: false, error: 'Pick a reason from the list or write your own.' };
  // Self-review is allowed but marked (Hugo Andrade, 2026-10-28): a Keep on one's own access (selfReview === true only,
  // not 'unchecked') needs a written reason of TEXT_MIN to TEXT_MAX characters; the TEXT_MAX bound is checked above.
  if (selfReview === true && decision === 'keep' && cpLen(t) < TEXT_MIN) {
    return { ok: false, error: `This is your own access: write why you should keep it in at least ${TEXT_MIN} characters.` };
  }
  if (hasRisk(item) && cpLen(t) < TEXT_MIN) {
    return { ok: false, error: `This assignment is flagged (${riskSignals(item).map((s) => SIGNALS[s].toLowerCase()).join(', ')}): write why in at least ${TEXT_MIN} characters.` };
  }
  if (reason === null && cpLen(t) < TEXT_MIN) return { ok: false, error: `Pick a reason or write one of at least ${TEXT_MIN} characters.` };
  return { ok: true, value: { decision, reasonId: reason, text: t || null } };
}

/** True when an item may be decided in a bulk action (spec 6.6): not GA, not PRA, no risk signal. */
export const bulkEligible = (item) => !NO_BULK_ROLE_IDS.includes(item?.roleTemplateId) && !hasRisk(item);
export const BULK_EXCLUDED_LINE = 'Global Administrator, Privileged Role Administrator and flagged assignments need one decision each.';

/** True when this decision needs the one-time code (decision 1): a Keep on GA or PRA made through an email link. */
export const needsStepUp = (item, decision, method) => decision === 'keep' && method === 'email-link' && STEP_UP_ROLE_IDS.includes(item?.roleTemplateId);

export const METHODS = Object.freeze(['signed-in', 'email-link', 'email-link+code']);
export const METHOD_WORDS = Object.freeze({ 'signed-in': 'Signed in to onGuard', 'email-link': 'Email link', 'email-link+code': 'Email link and one-time code' });

// ---- Reviewers -------------------------------------------------------------------------------------------------

export const REVIEWER_RULES = Object.freeze(['designated', 'manager', 'admin', 'admin-self', 'delegate', 'escalation']);
export const REVIEWER_RULE_WORDS = Object.freeze({ designated: 'Role reviewer', manager: 'Manager', admin: 'Administrator', delegate: 'Delegate', escalation: 'Escalated to administrator', 'admin-self': 'Administrator (own access)' });

const emailDomain = (a) => {
  const v = lower(a);
  const at = v.lastIndexOf('@');
  return at > 0 ? v.slice(at + 1) : '';
};
const covered = (domain, verified) => domain !== '' && (verified ?? []).some((d) => {
  const x = lower(typeof d === 'string' ? d : d?.name);
  return x !== '' && (domain === x || domain.endsWith(`.${x}`));
});

/**
 * The customer's verified domains as the engine reads them (the Watch domain check), or [] when there are none. Fails
 * closed to [] after a consent reset, and when the list records the tenant it was read from (tenantId, written since
 * 2026-10-29) and that is not the tenant on record (a tenant change): such a list is read again before it is used.
 */
export const customerVerifiedDomains = (customer) => {
  const stored = customer?.watchVerifiedDomains;
  const list = stored?.domains;
  if (!Array.isArray(list) || customer?.entraConsentResetAt) return [];
  if (typeof stored.tenantId === 'string' && stored.tenantId !== '' && lower(stored.tenantId) !== lower(customer?.entraTenantId)) return [];
  return list;
};

/** True when the address is in one of the verified domains (or a subdomain of one): the same rule as reviewerRefusal. */
export const addressInVerifiedDomains = (address, verifiedDomains) => covered(emailDomain(address), verifiedDomains);

/** The addresses of a person, lower case: mail, sign-in name, other mails and smtp proxy addresses. */
export function addressesOf(person) {
  const out = new Set();
  for (const v of [person?.mail, person?.upn, ...(Array.isArray(person?.addresses) ? person.addresses : [])]) {
    const a = lower(typeof v === 'string' ? v.replace(/^smtp:/i, '') : '');
    if (a.includes('@')) out.add(a);
  }
  return [...out];
}

/** The address onGuard writes to: mail when set, else the sign-in name. */
export const deliveryAddress = (person) => lower(person?.mail) || lower(person?.upn) || '';

/** True when two directory people are the same person: same id, or a shared address (an alternate account of one person). */
export function samePerson(a, b) {
  if (!a || !b) return false;
  if (lower(a.id) !== '' && lower(a.id) === lower(b.id)) return true;
  const theirs = new Set(addressesOf(b));
  return addressesOf(a).some((x) => theirs.has(x));
}

/**
 * The scope label the portal account may keep (spec section 6): only whether the role covers the whole directory. The
 * full label (an app or administrative unit name) is read in the customer's account at view time.
 */
export const coarseScopeLabel = (directoryScopeId) => (directoryScopeId === '/' ? 'Whole directory' : 'Part of the directory');

/** The full scope label of each assignment key in a detail file (`detail.assignments`); empty without one. */
export function scopeLabelsOf(detail) {
  return new Map((Array.isArray(detail?.assignments) ? detail.assignments : []).filter((a) => a && typeof a.key === 'string' && typeof a.scopeLabel === 'string').map((a) => [a.key, a.scopeLabel]));
}

/**
 * Whether a decider is reviewing access they hold (Hugo Andrade, 2026-10-28: self-review is allowed, so reviews never
 * get stuck, and is marked in the report as an observation). True for the same person (same id or a shared address) and
 * for a member of a group holder; 'unchecked' when the holder is a group whose member list is incomplete and does not
 * list the decider (membership is unknown, so it is neither claimed nor denied), or a user holder when the directory
 * could not be read (`directoryRead: false`); false otherwise.
 */
export function selfReviewOf(decider, holder, { directoryRead = true } = {}) {
  if (!decider || !holder) return false;
  if (samePerson(decider, holder)) return true;
  // The directory could not be read: an alternate account of the decider (a shared address) cannot be ruled out, so a
  // user holder with another id is 'unchecked', never a silent false (fail closed).
  if (directoryRead !== true && holder.type === 'user') return 'unchecked';
  if (holder.type === 'group') {
    if ((holder.members ?? []).map(lower).includes(lower(decider.id))) return true;
    // No membership data (missing list, unknown, or incomplete): self-review is not checked (spec section 4).
    if (holder.membersTruncated !== false || holder.membersUnknown === true || !Array.isArray(holder.members)) return 'unchecked';
  }
  return false;
}

/**
 * Why a candidate cannot review an item, or null when valid (spec 6.3). `holder` is the principal of the item (a user,
 * a group with `members`, or an app with `ownerIds`); `verifiedDomains` is the tenant's verified domain list, and
 * without it nobody is valid (fail closed, the Watch rule).
 */
export function reviewerRefusal(candidate, { holder, verifiedDomains } = {}) {
  if (!candidate || !isGuid(candidate.id)) return 'not-found';
  if (candidate.accountEnabled === false) return 'disabled';
  if (candidate.userType !== 'Member') return 'guest';
  const address = deliveryAddress(candidate);
  if (!address) return 'no-address';
  if (!Array.isArray(verifiedDomains) || verifiedDomains.length === 0) return 'no-verified-domains';
  if (!covered(emailDomain(address), verifiedDomains)) return 'address-outside-verified-domains';
  if (holder && samePerson(candidate, holder)) return 'self';
  if (holder?.type === 'group' && (holder.members ?? []).map(lower).includes(lower(candidate.id))) return 'member-of-group';
  // An owner of the app or of the role-assignable group controls the access under review (security review M2).
  if ((holder?.ownerIds ?? []).map(lower).includes(lower(candidate.id))) return 'owner';
  return null;
}

/**
 * What the admin is told when a designated reviewer is refused (reviewerRefusal codes). Each cause has its own copy:
 * 'no-verified-domains' means onGuard has no list from Microsoft yet, never that the address is wrong.
 */
const REFUSAL_MESSAGES = Object.freeze({
  guest: 'A reviewer must be a member of your directory, not a guest.',
  disabled: 'That account is disabled.',
  'no-address': 'That account has no email address, so onGuard cannot send it a review. Choose someone with a mailbox.',
  'no-verified-domains': 'onGuard could not read your verified domains from Microsoft yet. Try again in a few minutes.',
  'address-outside-verified-domains': 'A reviewer needs an email address in one of your verified domains.',
});
export const reviewerRefusalMessage = (code) => REFUSAL_MESSAGES[code] ?? 'That person cannot be a reviewer.';

/**
 * The reviewer of one item (spec 6.3): designated reviewer for the role, then the holder's manager (users only), then
 * the first valid administrator. `people` maps directory id to person; `admins` lists administrator people (resolved
 * from the directory by the caller). Returns { reviewerId, rule, skipped } or { reviewerId: null, reason:
 * 'no-independent-reviewer', skipped }.
 */
export function resolveReviewer({ item, holder, designated = {}, people = {}, admins = [], verifiedDomains, exclude = [] } = {}) {
  const skipped = [];
  const excluded = new Set(exclude.map(lower));
  const tryOne = (person, rule) => {
    if (!person) return null;
    if (excluded.has(lower(person.id))) { skipped.push({ rule, reason: 'excluded' }); return null; }
    const why = reviewerRefusal(person, { holder, verifiedDomains });
    if (why) { skipped.push({ rule, reason: why }); return null; }
    return { reviewerId: lower(person.id), rule, skipped };
  };
  const designatedId = lower(designated?.[item?.roleTemplateId]?.directoryId ?? designated?.[item?.roleTemplateId]);
  // Fail closed on a group whose members are not all known: anyone might be a member, so only an administrator reviews it.
  const unknownMembers = holder?.type === 'group' && holder.membersTruncated !== false;
  if (unknownMembers && designatedId) skipped.push({ rule: 'designated', reason: 'group-members-unknown' });
  const pick = (!unknownMembers && designatedId && tryOne(people[designatedId] ?? { id: designatedId }, 'designated'))
    || (holder?.type === 'user' && holder.managerId && tryOne(people[lower(holder.managerId)], 'manager'))
    || admins.map((a) => tryOne(a, 'admin')).find(Boolean);
  return pick ?? { reviewerId: null, reason: 'no-independent-reviewer', skipped };
}

// ---- Schedule and states -----------------------------------------------------------------------------------------

// Hugo Andrade, 2026-10-26: "Do not email flood by any means. It should be quarterly, or as configured by the admins."
// Quarterly by default; the admin may choose every six months or every year, or no schedule. Nothing shorter.
export const CADENCES = Object.freeze({ quarterly: 3, semiannual: 6, annual: 12, off: 0 });
export const CADENCE_WORDS = Object.freeze({ quarterly: 'every quarter', semiannual: 'every six months', annual: 'every year', off: 'not scheduled' });

/**
 * The email budget (Hugo Andrade, 2026-10-26). Every Access Reviews email obeys all of these; the one-time code is the
 * only exception to the daily cap and has its own limit (3 per link per hour).
 *   perReviewerPerCampaign  at most 4 emails to one reviewer in one campaign: the invitation, one reminder (automatic or
 *                           manual), one final reminder 2 days before due, one overdue or escalation notice
 *   perRecipientPerDay      at most 1 Access Reviews email to one address per day; what does not fit waits for the next day
 *   manualRemindDays        a manual reminder at most once per campaign per 7 days (it uses the reminder slot)
 *   manualStartDays         a campaign started by hand at most once per 30 days
 *   admin notices           note-due and overdue-removal notices at most once per item ever, batched into one email per
 *                           administrator per day with every other pending notice
 */
export const EMAIL_BUDGET = Object.freeze({ perReviewerPerCampaign: 4, perRecipientPerDay: 1, manualRemindDays: 7, manualStartDays: 30, codesPerLinkPerHour: 3 });
/** The reviewer email slot of each kind: each slot at most once per reviewer per campaign. */
export const EMAIL_SLOTS = Object.freeze({ invite: 'invite', delegated: 'invite', reminder: 'reminder', 'last-reminder': 'last-reminder', escalation: 'escalation', 'new-link': 'new-link' });
export const WINDOW_MIN = 7;
export const WINDOW_MAX = 30;
export const DEFAULT_SETTINGS = Object.freeze({
  schedule: Object.freeze({ cadence: 'quarterly', weekday: 1, windowDays: 14 }),
  roles: Object.freeze({ mode: 'default', roleTemplateIds: Object.freeze([]) }),
  reviewers: Object.freeze({}),
  expected: Object.freeze({}),
  removalWindowDays: 7,
  verificationWindowDays: 7,
});
export const FRESH_HOURS = 30;
export const LINK_GRACE_DAYS = 3;
export const MAX_LINK_DAYS = 33;
export const MAX_ITEMS_PER_CAMPAIGN = 1000;

const DAY = 86_400_000;
const utcDay = (y, m, d) => Date.UTC(y, m, d);

/** The nth weekday (0 Sunday to 6 Saturday) of a month, as a UTC midnight timestamp. */
function nthWeekday(year, month, weekday, n) {
  const first = new Date(utcDay(year, month, 1)).getUTCDay();
  return utcDay(year, month, 1 + ((weekday - first + 7) % 7) + (n - 1) * 7);
}

/**
 * The next scheduled start strictly after `now` (spec 6.2): the second `weekday` of the cadence's months (quarterly:
 * Jan, Apr, Jul, Oct), at 09:00 UTC. Null when the cadence is off.
 */
export function nextStartDate(schedule, now = new Date()) {
  const step = CADENCES[schedule?.cadence] ?? 0;
  if (!step) return null;
  const weekday = Number.isInteger(schedule?.weekday) && schedule.weekday >= 0 && schedule.weekday <= 6 ? schedule.weekday : 1;
  const t = now instanceof Date ? now.getTime() : Number(now);
  const d = new Date(t);
  for (let i = 0; i < 26; i++) {
    const y = d.getUTCFullYear() + Math.floor((d.getUTCMonth() + i) / 12);
    const m = (d.getUTCMonth() + i) % 12;
    if (m % step !== 0) continue;
    const start = nthWeekday(y, m, weekday, 2) + 9 * 3_600_000;
    if (start > t) return new Date(start);
  }
  return null;
}

/** Due date: the start plus the review window, at the end of that day (UTC). */
export function dueDateFor(openedAt, windowDays = 14) {
  const t = Date.parse(openedAt);
  const days = Math.min(WINDOW_MAX, Math.max(WINDOW_MIN, Number.isInteger(windowDays) ? windowDays : 14));
  const end = new Date(t + days * DAY);
  return new Date(utcDay(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()) + DAY - 1000);
}

/**
 * Link expiry (spec 7.2): due plus 3 days, at most 33 days after it is minted. A link minted after the due date (an
 * escalation or a new link for an overdue review) still lives 3 days from now.
 */
export function linkExpiry(dueAt, nowMs) {
  const wanted = Math.max(Date.parse(dueAt) + LINK_GRACE_DAYS * DAY, nowMs + LINK_GRACE_DAYS * DAY);
  return new Date(Math.min(wanted, nowMs + MAX_LINK_DAYS * DAY));
}

/** A readable campaign name: "Q4 2026" for a quarterly campaign opened in October 2026, else the month and year. */
export function campaignName(openedAt, cadence = 'quarterly') {
  const d = new Date(openedAt);
  const year = d.getUTCFullYear();
  if (cadence === 'quarterly') return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${year}`;
  if (cadence === 'semiannual') return `H${d.getUTCMonth() < 6 ? 1 : 2} ${year}`;
  if (cadence === 'annual') return `${year}`;
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export const CAMPAIGN_STATES = Object.freeze(['scheduled', 'open', 'overdue', 'closing', 'closed', 'cancelled']);
export const STATE_WORDS = Object.freeze({ scheduled: 'Scheduled', open: 'Open', overdue: 'Overdue', closing: 'Verifying removals', closed: 'Closed', cancelled: 'Cancelled' });
const TRANSITIONS = Object.freeze({
  scheduled: ['open', 'cancelled'],
  open: ['overdue', 'closing', 'closed', 'cancelled'],
  overdue: ['closing', 'closed', 'cancelled'],
  closing: ['closed'],
  closed: [],
  cancelled: [],
});
export const canTransition = (from, to) => (TRANSITIONS[from] ?? []).includes(to);
export const ACTIVE_STATES = Object.freeze(['open', 'overdue', 'closing']);
export const DECIDABLE_STATES = Object.freeze(['open', 'overdue']);

/** Item verification states (spec 7.1). */
export const VERIFICATION_STATES = Object.freeze(['pending', 'verified-removed', 'still-assigned', 'changed-kind', 'reopened']);

// ---- AC-6(7) -----------------------------------------------------------------------------------------------------

export const AC67_PERIOD_DAYS = 92;

/**
 * The AC-6(7) result of a closed campaign (spec 9.2): `met` when it covered the privileged roles, every item was decided
 * and every removal verified; `partially-met` when it closed with exceptions; `not-met` when it did not cover the
 * privileged roles or nothing was decided.
 */
export function ac67Result({ coveredRoleIds = [], counts = {} } = {}) {
  const covered = new Set(coveredRoleIds);
  const privileged = DEFAULT_ROLE_IDS.filter((r) => r !== GLOBAL_READER);
  if (!privileged.every((r) => covered.has(r))) return 'not-met';
  const total = counts.total ?? 0;
  const decided = (counts.kept ?? 0) + (counts.removed ?? 0);
  if (total > 0 && decided === 0) return 'not-met';
  // An assignment changed from active to eligible (or back) after a Remove is not a verified removal: it is an exception,
  // shown on its own in the evidence (changedKind), never counted as removed.
  const exceptions = (counts.noDecision ?? 0) + (counts.removalsNotVerified ?? 0) + (counts.changedKind ?? 0);
  return exceptions === 0 ? 'met' : 'partially-met';
}
export const AC67_WORDS = Object.freeze({ met: 'Met', 'partially-met': 'Partially met', 'not-met': 'Not met' });

// ---- Settings and notes ----------------------------------------------------------------------------------------

const SETTINGS_KEYS = new Set(['schedule', 'roles', 'reviewers', 'expected', 'removalWindowDays', 'verificationWindowDays', 'version']);
export const MAX_EXPECTED = 500;

/**
 * The setup panel (spec 6.2, 6.3; Hugo item 5): schedule, roles in scope, designated reviewers ({ directoryId } picked
 * from the people search, checked by the route through a directory lookup; null removes one), optional expected counts. Strict: unknown keys refused. Returns { ok, value }
 * or { ok: false, error }. `value` holds only the fields given (a partial update).
 */
export function validateSettings(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return { ok: false, error: 'The request body is not valid.' };
  if (!Object.keys(body).every((k) => SETTINGS_KEYS.has(k))) return { ok: false, error: 'Unknown field in the request.' };
  const value = {};
  if (body.schedule !== undefined) {
    const s = body.schedule;
    if (!s || typeof s !== 'object' || !Object.hasOwn(CADENCES, s.cadence)) return { ok: false, error: 'Pick how often to review: every quarter, every six months, every year, or not scheduled. Reviews are never more frequent than quarterly.' };
    if (s.weekday !== undefined && !(Number.isInteger(s.weekday) && s.weekday >= 0 && s.weekday <= 6)) return { ok: false, error: 'Pick a start weekday.' };
    if (s.windowDays !== undefined && !(Number.isInteger(s.windowDays) && s.windowDays >= WINDOW_MIN && s.windowDays <= WINDOW_MAX)) return { ok: false, error: `Reviewers get ${WINDOW_MIN} to ${WINDOW_MAX} days to decide.` };
    value.schedule = { cadence: s.cadence, weekday: s.weekday ?? 1, windowDays: s.windowDays ?? 14 };
  }
  if (body.roles !== undefined) {
    const r = body.roles;
    if (!r || !['default', 'every', 'custom'].includes(r.mode)) return { ok: false, error: 'Pick which roles to review.' };
    const ids = r.mode === 'custom' ? r.roleTemplateIds : [];
    if (!Array.isArray(ids) || ids.length > 200 || !ids.every(isGuid)) return { ok: false, error: 'A role in the list is not a directory role.' };
    if (r.mode === 'custom' && ids.length === 0) return { ok: false, error: 'Pick at least one role to review.' };
    value.roles = { mode: r.mode, roleTemplateIds: [...new Set(ids.map(lower))] };
  }
  if (body.reviewers !== undefined) {
    const r = body.reviewers;
    if (!r || typeof r !== 'object' || Array.isArray(r) || Object.keys(r).length > 200) return { ok: false, error: 'The role reviewers are not valid.' };
    const out = {};
    for (const [roleId, v] of Object.entries(r)) {
      if (!isGuid(roleId)) return { ok: false, error: 'A role reviewer is set for something that is not a directory role.' };
      if (v === null) { out[lower(roleId)] = null; continue; }
      // A directory id picked from the people search (callers who see names), or a typed sign-in name or email that the
      // route resolves (callers who may edit but never see names: staff acting on the top tier). One shape per entry.
      if (!v || typeof v !== 'object' || Array.isArray(v) || Object.keys(v).length !== 1) return { ok: false, error: 'Pick the reviewer from the suggestions of your directory.' };
      if (Object.hasOwn(v, 'directoryId')) {
        if (!isGuid(v.directoryId)) return { ok: false, error: 'Pick the reviewer from the suggestions of your directory.' };
        out[lower(roleId)] = { directoryId: lower(v.directoryId) };
      } else if (Object.hasOwn(v, 'lookup')) {
        const key = typeof v.lookup === 'string' ? v.lookup.trim() : '';
        if (key.length < 3 || key.length > 256) return { ok: false, error: 'Type the reviewer\'s sign-in name or email address.' };
        out[lower(roleId)] = { lookup: key };
      } else return { ok: false, error: 'Pick the reviewer from the suggestions of your directory.' };
    }
    value.reviewers = out;
  }
  if (body.expected !== undefined) {
    const e = body.expected;
    if (!e || typeof e !== 'object' || Array.isArray(e) || Object.keys(e).length > 200) return { ok: false, error: 'The expected counts are not valid.' };
    const out = {};
    for (const [roleId, n] of Object.entries(e)) {
      if (!isGuid(roleId)) return { ok: false, error: 'An expected count is set for something that is not a directory role.' };
      // Blank means "no expectation" (Hugo item 5): null removes it.
      if (n === null || n === '') { out[lower(roleId)] = null; continue; }
      if (!Number.isInteger(n) || n < 0 || n > MAX_EXPECTED) return { ok: false, error: `An expected count is a whole number from 0 to ${MAX_EXPECTED}, or blank for no expectation.` };
      out[lower(roleId)] = n;
    }
    value.expected = out;
  }
  for (const k of ['removalWindowDays', 'verificationWindowDays']) {
    if (body[k] === undefined) continue;
    if (!(Number.isInteger(body[k]) && body[k] >= 1 && body[k] <= 30)) return { ok: false, error: 'The removal and verification windows are 1 to 30 days.' };
    value[k] = body[k];
  }
  return { ok: true, value };
}

/** Stored settings over the defaults. Expected counts stay blank unless set (Hugo item 5). */
export function normalizeSettings(stored) {
  const s = stored && typeof stored === 'object' ? stored : {};
  const expected = {};
  for (const [k, v] of Object.entries(s.expected ?? {})) if (isGuid(k) && Number.isInteger(v)) expected[lower(k)] = v;
  const reviewers = {};
  for (const [k, v] of Object.entries(s.reviewers ?? {})) if (isGuid(k) && v && isGuid(v.directoryId)) reviewers[lower(k)] = { directoryId: lower(v.directoryId) };
  return {
    schedule: { ...DEFAULT_SETTINGS.schedule, ...(s.schedule ?? {}), ...(Object.hasOwn(CADENCES, s.schedule?.cadence) ? {} : { cadence: DEFAULT_SETTINGS.schedule.cadence }) },
    roles: { mode: ['default', 'every', 'custom'].includes(s.roles?.mode) ? s.roles.mode : 'default', roleTemplateIds: Array.isArray(s.roles?.roleTemplateIds) ? s.roles.roleTemplateIds.filter(isGuid) : [] },
    reviewers,
    expected,
    removalWindowDays: Number.isInteger(s.removalWindowDays) ? s.removalWindowDays : DEFAULT_SETTINGS.removalWindowDays,
    verificationWindowDays: Number.isInteger(s.verificationWindowDays) ? s.verificationWindowDays : DEFAULT_SETTINGS.verificationWindowDays,
    version: Number.isInteger(s.version) ? s.version : 0,
  };
}

/** The role template ids a campaign covers, given the settings and the roles the tenant has. */
export function rolesInScope(settings, tenantRoles = []) {
  const s = normalizeSettings(settings);
  if (s.roles.mode === 'every') return [...new Set(tenantRoles.map((r) => lower(r.roleTemplateId)).filter(isGuid))];
  if (s.roles.mode === 'custom') return s.roles.roleTemplateIds;
  return [...DEFAULT_ROLE_IDS];
}

export const NOTE_MAX = 500;
export const MAX_NOTES = 500;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const NOTE_KEYS = new Set(['assignmentKey', 'text', 'reviewBy', 'owner', 'version']);

/**
 * A note on a role assignment (Hugo item 6): "Camila is Global Reader temporarily for the onGuard apps project; review
 * when the project ends", an optional review-by date and an optional owner. Notes are customer data: they are stored in
 * the customer's own AWS account (lib/access-review-notes.mjs), never in the portal database. Returns { ok, value } or
 * { ok: false, error }. `owner` is a lookup string the route resolves and pins to a directory id.
 */
export function validateNote(body, { now = new Date() } = {}) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return { ok: false, error: 'The request body is not valid.' };
  if (!Object.keys(body).every((k) => NOTE_KEYS.has(k))) return { ok: false, error: 'Unknown field in the request.' };
  const key = String(body.assignmentKey ?? '');
  const [principalId, roleTemplateId, scope, kind] = key.split('|');
  if (!isGuid(principalId) || !isGuid(roleTemplateId) || !isScope(scope) || !KINDS.includes(kind)) return { ok: false, error: 'Pick the role assignment the note is about.' };
  const text = cleanText(body.text);
  if (cpLen(text) < 3) return { ok: false, error: 'Write the note: why this person holds the role, and when to look at it again.' };
  if (cpLen(text) > NOTE_MAX) return { ok: false, error: `Keep the note under ${NOTE_MAX} characters.` };
  if (looksSecret(text)) return { ok: false, error: SECRET_TEXT_MESSAGE };
  let reviewBy = null;
  if (body.reviewBy !== undefined && body.reviewBy !== null && body.reviewBy !== '') {
    const t = Date.parse(`${body.reviewBy}T00:00:00Z`);
    if (typeof body.reviewBy !== 'string' || !DATE_ONLY.test(body.reviewBy) || Number.isNaN(t)) return { ok: false, error: 'The review-by date is a calendar date.' };
    if (t > now.getTime() + 3 * 366 * DAY) return { ok: false, error: 'Pick a review-by date within three years.' };
    reviewBy = body.reviewBy;
  }
  let owner = null;
  if (body.owner !== undefined && body.owner !== null && body.owner !== '') {
    if (typeof body.owner !== 'string' || body.owner.length < 3 || body.owner.length > 256) return { ok: false, error: 'Type the owner\'s sign-in name or email address, or leave it blank.' };
    owner = body.owner.trim();
  }
  return { ok: true, value: { assignmentKey: assignmentKey({ principalId, roleTemplateId, directoryScopeId: scope, kind }), text, reviewBy, owner } };
}

/** True when a note's review-by date has passed (the day after it, UTC). */
export const noteDue = (note, now = new Date()) => typeof note?.reviewBy === 'string' && DATE_ONLY.test(note.reviewBy) && Date.parse(`${note.reviewBy}T00:00:00Z`) + DAY <= now.getTime();

/** A stored note, bounded and cleaned (what the notes item in the customer's account may hold). */
export function normalizeNote(n) {
  if (!n || typeof n !== 'object') return null;
  const noteId = typeof n.noteId === 'string' && /^n-[0-9a-f]{12}$/.test(n.noteId) ? n.noteId : null;
  const parts = String(n.assignmentKey ?? '').split('|');
  if (!noteId || parts.length !== 4 || !isGuid(parts[0]) || !isGuid(parts[1])) return null;
  const text = cleanText(n.text).slice(0, NOTE_MAX);
  if (!text) return null;
  return {
    noteId,
    assignmentKey: n.assignmentKey,
    text,
    reviewBy: typeof n.reviewBy === 'string' && DATE_ONLY.test(n.reviewBy) ? n.reviewBy : null,
    ownerId: isGuid(n.ownerId) ? lower(n.ownerId) : null,
    createdAt: typeof n.createdAt === 'string' ? n.createdAt.slice(0, 40) : null,
    updatedAt: typeof n.updatedAt === 'string' ? n.updatedAt.slice(0, 40) : null,
  };
}

// ---- Findings between campaigns ----------------------------------------------------------------------------------

export const FINDING_IDS = Object.freeze(['ARV-001', 'ARV-002', 'ARV-003', 'ARV-004', 'ARV-005', 'ARV-006', 'ARV-007']);
/** Kinds of expectation. v1 is a per-role count; phase 2 adds role bundles (spec 18). */
export const EXPECTATION_KINDS = Object.freeze(['role-count']);
const SEV_RANK = { high: 0, medium: 1, low: 2, info: 3 };
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '');

/**
 * "Findings between campaigns" (spec 8.2), from counts and ids only, so the viewer and the operator get the same rows
 * as the admin without a name. Each row: { id, sentence, severity, evaluated, reason?, count, nextStep, action? }.
 *
 * Inputs:
 *   summary      the sanitized run summary (sanitizeAccessReviewSummary) or null
 *   byRole       holders per role template id (from the summary)
 *   expected     the admin's expected counts (blank by default: no ARV-001 row without one, Hugo item 5)
 *   lastClosed   { closedAt, name, assignmentTags[] } of the last closed campaign, or null
 *   currentTags  the key tags of today's assignments in scope (ARV-003), or null when not known
 *   schedule     the normalized schedule
 *   reopened     count of reopened items (ARV-005), removalsOverdue: [{ days, globalAdmin }] (ARV-006)
 *   notesDue     count of notes past their review-by date (ARV-007)
 *   globalAdmins the Global Administrator count (the IDENT-003 guidance row, a check, not an expectation)
 */
export function buildFindings({ summary = null, expected = {}, lastClosed = null, currentTags = null, schedule = DEFAULT_SETTINGS.schedule, reopened = 0, removalsOverdue = [], notesDue = 0, nextStart = null, campaignOpen = false, now = new Date(), roles = [] } = {}) {
  const rows = [];
  const byRole = summary?.byRole ?? {};
  // IDENT-003 guidance: a check (Microsoft "fewer than five"), shown whatever the admin set.
  const ga = summary ? (byRole[GLOBAL_ADMINISTRATOR] ?? 0) : null;
  if (ga === null) rows.push({ id: 'IDENT-003', sentence: 'Global Administrator count', severity: 'info', evaluated: false, reason: 'No role assignment collection yet.', count: 0, nextStep: '' });
  else {
    const sev = ga < 2 || ga > 8 ? 'high' : ga > 4 ? 'medium' : null;
    rows.push({
      id: 'IDENT-003',
      sentence: `${plural(ga, 'Global Administrator')}. Microsoft guidance: 2 to 4 (two can be emergency access accounts)`,
      severity: sev ?? 'info', evaluated: true, count: ga, ok: sev === null,
      nextStep: sev ? (ga < 2 ? 'Add a second Global Administrator, for example an emergency access account' : 'Review the Global Administrators and move daily work to narrower roles') : '',
    });
  }
  // ARV-001: only where the admin set an expectation.
  for (const [roleId, exp] of Object.entries(expected ?? {})) {
    if (!Number.isInteger(exp) || !summary) continue;
    const n = byRole[roleId] ?? 0;
    if (n <= exp) continue;
    rows.push({
      id: 'ARV-001', roleTemplateId: roleId,
      sentence: `${plural(n, roleName(roleId, roles))}, above your expected ${exp}`,
      severity: n <= Math.ceil(exp * 1.2) ? 'low' : 'medium', evaluated: true, count: n - exp,
      nextStep: 'Review in the next campaign, or start one now for this role',
    });
  }
  // ARV-002: sign-in signal (P1 sign-in activity), from the collector's counts.
  const a2 = summary?.arv002;
  if (!summary || a2?.evaluated === false) rows.push({ id: 'ARV-002', sentence: 'Role holders without a recent sign-in', severity: 'info', evaluated: false, reason: summary ? 'Not evaluated: sign-in activity needs Microsoft Entra ID P1 and the AuditLog.Read.All permission.' : 'No role assignment collection yet.', count: 0, nextStep: '' });
  else if (a2.count > 0) rows.push({ id: 'ARV-002', sentence: `${plural(a2.count, 'role holder')} never signed in or not in 90 days`, severity: a2.globalAdmin > 0 ? 'high' : 'medium', evaluated: true, count: a2.count, nextStep: 'Confirm with the manager, then remove the role' });
  // ARV-003: added since the last closed campaign.
  if (lastClosed && Array.isArray(lastClosed.assignmentTags) && Array.isArray(currentTags)) {
    const before = new Set(lastClosed.assignmentTags);
    const added = currentTags.filter((t) => !before.has(t)).length;
    if (added > 0) rows.push({ id: 'ARV-003', sentence: `${plural(added, 'privileged assignment')} added since the last review`, severity: campaignOpen ? 'info' : 'low', evaluated: true, count: added, nextStep: nextStart ? `They join the next campaign on ${fmtDate(nextStart)}. Start now to review them sooner` : 'Start a review now to vouch for them' });
  }
  // ARV-004: no review closed within the period.
  const period = (CADENCES[schedule?.cadence] ? CADENCES[schedule.cadence] * 30.5 : AC67_PERIOD_DAYS) + 7;
  const closedAgo = lastClosed?.closedAt ? (now.getTime() - Date.parse(lastClosed.closedAt)) / DAY : Infinity;
  if (closedAgo > Math.max(period, AC67_PERIOD_DAYS)) {
    const scheduled = (CADENCES[schedule?.cadence] ?? 0) > 0;
    rows.push({ id: 'ARV-004', sentence: `No privileged access review closed in the last ${Math.round(Math.max(period, AC67_PERIOD_DAYS))} days`, severity: scheduled && lastClosed ? 'high' : 'medium', evaluated: true, count: 1, nextStep: 'Start a review now', action: 'start' });
  }
  if (reopened > 0) rows.push({ id: 'ARV-005', sentence: `${plural(reopened, 'removed assignment')} came back after a review`, severity: 'high', evaluated: true, count: reopened, nextStep: 'Find who re-added it in Activity, then remove it again or record why' });
  if (removalsOverdue.length > 0) {
    const worst = Math.max(...removalsOverdue.map((r) => r.days ?? 0));
    rows.push({ id: 'ARV-006', sentence: `${plural(removalsOverdue.length, 'removal')} decided ${worst} days ago ${removalsOverdue.length === 1 ? 'is' : 'are'} still assigned`, severity: removalsOverdue.some((r) => r.globalAdmin) ? 'high' : 'medium', evaluated: true, count: removalsOverdue.length, nextStep: 'Remove it in the Microsoft Entra admin center' });
  }
  if (notesDue > 0) rows.push({ id: 'ARV-007', sentence: `${plural(notesDue, 'note')} past ${notesDue === 1 ? 'its' : 'their'} review-by date`, severity: 'low', evaluated: true, count: notesDue, nextStep: 'Look at the assignment again: keep it with a new date, or remove the role' });
  // Problems first by severity, then the larger count; rows not evaluated last, kept with their reason.
  return rows.sort((x, y) => (x.evaluated === y.evaluated ? 0 : x.evaluated ? -1 : 1)
    || (x.ok === true) - (y.ok === true)
    || (SEV_RANK[x.severity] ?? 9) - (SEV_RANK[y.severity] ?? 9)
    || (y.count ?? 0) - (x.count ?? 0));
}

/** The cross-service recommendation shape (License Insights spec section 8), the seam for a Recommendations list. */
export function accessReviewRecommendations(findings) {
  return (findings ?? []).filter((f) => f.evaluated && f.id.startsWith('ARV-')).map((f) => ({
    id: f.id, service: 'access-reviews', title: f.sentence, owner: 'Privileged Role Administrator',
    impact: { assignments: f.count, severity: f.severity }, steps: f.nextStep ? [f.nextStep] : [], links: [], evaluated: true, status: 'open',
  }));
}

// ---- Sanitizing what the collector sends and writes --------------------------------------------------------------

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const int = (v, max = 10_000_000) => (Number.isInteger(v) && v >= 0 && v <= max ? v : null);
const iso = (v) => (typeof v === 'string' && v.length <= 40 && !Number.isNaN(Date.parse(v)) ? v : null);
const REF_RE = /^i-[0-9a-f]{12}$/;
const NOTE_ID_RE = /^n-[0-9a-f]{12}$/;
export const MAX_SUMMARY_BYTES = 60_000;

/**
 * The run callback's `accessReviews` (counts and ids only, never a name; security review of PR 422, H1, applied here):
 * { complete, collectedAt, p2, assignments, inScope, byRole, signals, arv002, notesDue[], verification[], reappeared[] }.
 * Unknown fields are dropped; anything over MAX_SUMMARY_BYTES is null (the page then reads "pending").
 */
export function sanitizeAccessReviewSummary(raw, { onOversize } = {}) {
  if (!isObj(raw)) return null;
  const byRole = {};
  for (const [k, v] of Object.entries(isObj(raw.byRole) ? raw.byRole : {}).slice(0, 300)) if (isGuid(k) && int(v) !== null) byRole[lower(k)] = v;
  const signals = {};
  for (const code of SIGNAL_CODES) if (int(raw.signals?.[code]) !== null) signals[code] = raw.signals[code];
  const a2 = isObj(raw.arv002) ? raw.arv002 : null;
  const out = {
    complete: raw.complete === true,
    collectedAt: iso(raw.collectedAt),
    p2: raw.p2 === true,
    managerAvailable: raw.managerAvailable === true,
    assignments: int(raw.assignments) ?? 0,
    inScope: int(raw.inScope) ?? 0,
    byRole,
    signals,
    arv002: a2 ? { evaluated: a2.evaluated !== false, count: int(a2.count) ?? 0, globalAdmin: int(a2.globalAdmin) ?? 0 } : { evaluated: false, count: 0, globalAdmin: 0 },
    notesDue: (Array.isArray(raw.notesDue) ? raw.notesDue : []).filter((n) => typeof n === 'string' && NOTE_ID_RE.test(n)).slice(0, MAX_NOTES),
    verification: (Array.isArray(raw.verification) ? raw.verification : []).slice(0, 2000)
      .filter((v) => isObj(v) && typeof v.ref === 'string' && REF_RE.test(v.ref) && ['present', 'gone', 'changed-kind'].includes(v.state))
      .map((v) => ({ ref: v.ref, state: v.state })),
    reappeared: (Array.isArray(raw.reappeared) ? raw.reappeared : []).filter((r) => typeof r === 'string' && REF_RE.test(r)).slice(0, 2000),
  };
  const bytes = new TextEncoder().encode(JSON.stringify(out)).length;
  if (bytes > MAX_SUMMARY_BYTES) { try { onOversize?.(bytes); } catch { /* never changes the answer */ } return null; }
  return out;
}

const text = (v, max = 256) => {
  if (typeof v !== 'string') return null;
  const t = v.replace(UNSAFE, '').trim().slice(0, max).trim();
  return t === '' ? null : t;
};

function cleanPerson(p, id) {
  if (!isObj(p) || !isGuid(id)) return null;
  const type = ['user', 'group', 'servicePrincipal'].includes(p.type) ? p.type : 'user';
  return {
    id: lower(id),
    type,
    displayName: text(p.displayName) ?? (type === 'servicePrincipal' ? 'Unnamed app' : 'Unnamed account'),
    jobTitle: type === 'user' ? text(p.jobTitle, 128) : null,
    emergencyAccess: type === 'user' && p.emergencyAccess === true,
    upn: text(p.upn, 320),
    mail: text(p.mail, 320),
    addresses: Array.isArray(p.addresses) ? p.addresses.slice(0, 20).map((a) => text(a, 320)).filter(Boolean) : [],
    userType: p.userType === 'Guest' ? 'Guest' : p.userType === 'Member' ? 'Member' : null,
    accountEnabled: p.accountEnabled === false ? false : p.accountEnabled === true ? true : null,
    homeDomain: text(p.homeDomain, 253),
    managerId: isGuid(p.managerId) ? lower(p.managerId) : null,
    members: type === 'group' && Array.isArray(p.members) ? p.members.filter(isGuid).slice(0, 200).map(lower) : [],
    memberCount: int(p.memberCount) ?? null,
    ownerIds: (type === 'servicePrincipal' || type === 'group') && Array.isArray(p.ownerIds) ? p.ownerIds.filter(isGuid).slice(0, 20).map(lower) : [],
    // A group whose member list is not known in full (read failed, or more members than were kept) is treated as possibly
    // containing anyone: only an administrator outside the known members and owners may review it (security review M2).
    membersTruncated: type === 'group' ? p.membersTruncated !== false : false,
    lastSignInAt: iso(p.lastSignInAt),
    createdAt: iso(p.createdAt),
  };
}

/**
 * The bucket file `_access-review-insights/_latest.json` the collector writes (names live there, in the customer's
 * account). The portal reads it at request time through the trust role and never stores it. Bounded: at most 5000
 * assignments and 10000 people.
 */
export function sanitizeAccessReviewDetail(raw) {
  if (!isObj(raw) || !isObj(raw.meta)) return null;
  const roles = (Array.isArray(raw.roles) ? raw.roles : []).slice(0, 500).filter((r) => isObj(r) && isGuid(r.roleTemplateId)).map((r) => ({
    roleTemplateId: lower(r.roleTemplateId), name: text(r.name) ?? 'Directory role', privileged: r.privileged === true, builtIn: r.builtIn !== false,
  }));
  const assignments = (Array.isArray(raw.assignments) ? raw.assignments : []).slice(0, 5000).filter((a) => isObj(a) && isGuid(a.principalId) && isGuid(a.roleTemplateId)).map((a) => {
    const kind = a.kind === 'eligible' ? 'eligible' : 'active';
    const directoryScopeId = isScope(a.directoryScopeId) ? a.directoryScopeId : '/';
    return {
      key: assignmentKey({ principalId: a.principalId, roleTemplateId: a.roleTemplateId, directoryScopeId, kind }),
      principalId: lower(a.principalId),
      principalType: PRINCIPAL_TYPES.includes(a.principalType) ? a.principalType : 'unknown',
      roleTemplateId: lower(a.roleTemplateId),
      directoryScopeId,
      scopeLabel: text(a.scopeLabel) ?? (directoryScopeId === '/' ? 'Whole directory' : 'Part of the directory'),
      kind,
      signals: (Array.isArray(a.signals) ? a.signals : []).filter((s) => SIGNAL_CODES.includes(s)),
      daysSinceSignIn: int(a.daysSinceSignIn, 100_000),
      lastSignInAt: iso(a.lastSignInAt),
    };
  });
  const people = {};
  let n = 0;
  for (const [id, p] of Object.entries(isObj(raw.people) ? raw.people : {})) {
    if (n >= 10_000) break;
    const c = cleanPerson(p, id);
    if (c) { people[c.id] = c; n++; }
  }
  return {
    meta: {
      generatedAt: iso(raw.meta.generatedAt),
      runId: text(raw.meta.runId, 80),
      tenantId: isGuid(raw.meta.tenantId) ? lower(raw.meta.tenantId) : null,
      p2: raw.meta.p2 === true,
      complete: raw.meta.complete === true,
      managerAvailable: raw.meta.managerAvailable === true,
      signInAvailable: raw.meta.signInAvailable === true,
    },
    roles,
    assignments,
    people,
  };
}

/**
 * The Microsoft Entra admin center page for a role: Roles and administrators, the documented list of every directory
 * role (the same link shared/finding-guidance.mjs uses). The per-role RoleMenuBlade deep link showed an error in Entra
 * (Hugo Andrade, 2026-10-30) and its exact format is not documented by Microsoft, so it is not used; the copy around
 * this link names the role to pick. Callers may still pass the role template id; it is ignored.
 */
export function entraRoleUrl() {
  return 'https://entra.microsoft.com/#view/Microsoft_AAD_IAM/AllRolesBlade';
}

export const FOOTER_LINE = 'onGuard reads role assignments and never changes them. Removals happen in Microsoft Entra; onGuard verifies them on the next collection. This is not Microsoft Entra ID Governance access reviews.';
